import { useCallback, useState } from "react";
import type { TableColumnPref } from "@onengine/component-library";

const keyFor = (tableName: string) => `${tableName}-column-prefs`;

const read = (tableName: string): TableColumnPref[] | undefined => {
  try {
    const raw = window.localStorage.getItem(keyFor(tableName));
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return undefined;
    // Anything that is not a pref is dropped rather than handed to the table, so a value left
    // by an older version cannot break the header.
    const prefs = parsed.filter(
      (entry): entry is TableColumnPref =>
        typeof entry === "object" &&
        entry !== null &&
        typeof (entry as TableColumnPref).id === "string" &&
        typeof (entry as TableColumnPref).visible === "boolean",
    );
    return prefs.length > 0 ? prefs : undefined;
  } catch {
    // Private windows and blocked site data both throw here; the table just opens at its
    // own defaults.
    return undefined;
  }
};

/**
 * Remembers a table's column order, visibility and widths.
 *
 * The library keeps column prefs in component state only, so without this a resize is lost on
 * reload — unlike sorting, which it persists to localStorage itself under `{tableName}-sort-*`.
 * This matches that mechanism and that scope: **per browser**, not per person — the key has no
 * user id, so two people sharing a machine share the layout, exactly as they already share the
 * sort order. A saved view on the server would be the next step up, and is a decision for
 * every list rather than one of them.
 */
export function useTableColumnPrefs(tableName: string) {
  const [stored, setStored] = useState<TableColumnPref[] | undefined>(() => read(tableName));
  const [remountKey, setRemountKey] = useState(0);

  const onColumnPrefsChange = useCallback(
    (prefs: TableColumnPref[]) => {
      setStored(prefs);
      try {
        window.localStorage.setItem(keyFor(tableName), JSON.stringify(prefs));
      } catch {
        // Not being able to remember is not a reason to stop the resize working now.
      }
    },
    [tableName],
  );

  /**
   * Back to the table's own layout.
   *
   * Clearing the stored prefs is not enough on its own: the library reads `defaultColumnPrefs`
   * once, when it mounts, so the widths already on screen would stay until the next reload.
   * Bumping the key remounts it, which is what makes the reset visible immediately.
   */
  const resetColumns = useCallback(() => {
    setStored(undefined);
    setRemountKey((current) => current + 1);
    try {
      window.localStorage.removeItem(keyFor(tableName));
    } catch {
      // Already gone, or storage is blocked; the remount above is what the user sees.
    }
  }, [tableName]);

  // `stored` stays state rather than a plain read: the table remounts inside the same host
  // (the roles list swaps between its empty state and the table), and the prefs have to
  // survive that.
  return {
    defaultColumnPrefs: stored,
    onColumnPrefsChange,
    /**
     * Whether the layout differs from the table's own. Nothing is stored until the user
     * actually changes something — the library does not write prefs on mount — so the action
     * stays hidden for anyone who has never dragged a column.
     */
    canReset: stored !== undefined,
    resetColumns,
    tableKey: `${tableName}-${remountKey}`,
  };
}
